import { useState } from 'react';
import { adminApi } from '../api/admin';
import { useFranchiseesQuery } from '../api/queries';
import AfAccessEditor from './AfAccessEditor';
import { showToast } from './Toast';
import type { UserRow } from '../types';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

export default function UserCreateForm({ open, onClose, onCreated }: Props) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRow['role']>('franchisee');
  const [afNumbers, setAfNumbers] = useState<string[]>([]);
  const [sendInviteOnCreate, setSendInviteOnCreate] = useState(true);
  const [busy, setBusy] = useState(false);
  const { data: franchisees } = useFranchiseesQuery();

  if (!open) return null;

  function reset() {
    setEmail('');
    setName('');
    setRole('franchisee');
    setAfNumbers([]);
    setSendInviteOnCreate(true);
    setBusy(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) {
      showToast('Email required', { variant: 'error' });
      return;
    }
    setBusy(true);
    try {
      await adminApi.createUser({
        email: email.trim().toLowerCase(),
        name: name.trim() || undefined,
        role,
        afNumbers: role === 'admin' ? [] : afNumbers,
      });
      if (sendInviteOnCreate) {
        try {
          await adminApi.sendInvite({
            email: email.trim().toLowerCase(),
            name: name.trim() || undefined,
            role,
          });
          showToast(`User created and invited (${email})`);
        } catch (err) {
          showToast(`User created — invite failed: ${(err as Error).message}`, {
            variant: 'error',
          });
        }
      } else {
        showToast(`User created (${name || email})`);
      }
      reset();
      onCreated();
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="ds-form-inline open" onSubmit={handleSubmit}>
      <div className="ds-form-inline-title">Create New User</div>
      <div className="ds-form-grid">
        <div className="ds-field">
          <label htmlFor="newUserEmail">Email</label>
          <input
            id="newUserEmail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@amadafranchise.com"
            required
            autoFocus
          />
        </div>
        <div className="ds-field">
          <label htmlFor="newUserName">Full Name</label>
          <input
            id="newUserName"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First Last"
          />
        </div>
        <div className="ds-field">
          <label htmlFor="newUserRole">Role</label>
          <select
            id="newUserRole"
            value={role}
            onChange={(e) => setRole(e.target.value as UserRow['role'])}
          >
            <option value="franchisee">Franchisee</option>
            <option value="fbc">FBC</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>
      {role !== 'admin' && (
        <div className="ds-field" style={{ marginTop: 4, marginBottom: 12 }}>
          <label>
            Franchise Access{role === 'fbc' ? ' (assignable AFs)' : ''}
          </label>
          <AfAccessEditor
            value={afNumbers}
            onChange={setAfNumbers}
            franchisees={franchisees}
            placeholder="AF-150, Twin Cities, etc."
          />
        </div>
      )}
      <label className="ds-checkbox">
        <input
          type="checkbox"
          checked={sendInviteOnCreate}
          onChange={(e) => setSendInviteOnCreate(e.target.checked)}
        />
        Send invitation email on create
      </label>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 }}>
        <button type="submit" className="ds-btn ds-btn-primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create User'}
        </button>
        <button
          type="button"
          className="ds-btn ds-btn-secondary"
          onClick={() => {
            reset();
            onClose();
          }}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
